import type {
	ICellRendererParams,
	ValueFormatterParams,
} from 'ag-grid-community';

import { RateCellRender } from '../RateCellRender';
import {
	oneDecimalFormatter,
	renderLiveData,
	thousandSeparatorRateFormatter,
	twoDecimalRateFormatter,
} from '../tableCustomCellRender';
import type { SourceColumn } from '../useDashboardColumns';

import { LiveDataField } from 'components/live-data-field';
import { labels } from 'locales/en.label';
import { ColumnSources } from './types';
import { liveDataComparator } from './utils';

export const kpiColsData: SourceColumn[] = [
	{
		headerName: 'ADR\n(LYST)',
		field: 'adr_lyst',
		headerTooltip: labels.rec_review.dashboard.tooltip.adr_lyst,
		cellRenderer: (params: ICellRendererParams) => {
			return (
				<RateCellRender
					params={params}
					digitFormatter={twoDecimalRateFormatter}
				/>
			);
		},
		source: ColumnSources.KPI,
	},
	{
		headerName: 'ADR\n(LYF)',
		field: 'adr_lyf',
		headerTooltip: labels.rec_review.dashboard.tooltip.adr_lyf,
		cellRenderer: (params: ICellRendererParams) => {
			return (
				<RateCellRender
					params={params}
					digitFormatter={twoDecimalRateFormatter}
				/>
			);
		},
		source: ColumnSources.KPI,
	},
	{
		headerName: 'RevPAR',
		field: 'revpar_live',
		headerTooltip: labels.rec_review.dashboard.tooltip.revpar,
		cellRenderer: (params: ICellRendererParams) => {
			return renderLiveData(
				{ ...params },
				'revpar_live',
				'revpar',
				true,
				twoDecimalRateFormatter,
			);
		},
		comparator: liveDataComparator('revpar'),
		source: ColumnSources.KPI,
	},
	{
		headerName: 'RevPAR\n(LYST)',
		field: 'revpar_lyst',
		headerTooltip: labels.rec_review.dashboard.tooltip.revpar_lyst,
		cellRenderer: (params: ICellRendererParams) => {
			return (
				<RateCellRender
					params={params}
					digitFormatter={twoDecimalRateFormatter}
				/>
			);
		},
		source: ColumnSources.KPI,
	},
	{
		headerName: 'RevPAR\n(LYF)',
		field: 'revpar_lyf',
		headerTooltip: labels.rec_review.dashboard.tooltip.revpar_lyf,
		cellRenderer: (params: ICellRendererParams) => {
			return (
				<RateCellRender
					params={params}
					digitFormatter={twoDecimalRateFormatter}
				/>
			);
		},
		source: ColumnSources.KPI,
	},
	{
		headerName: 'Occ %',
		field: 'occ_pct_live',
		headerTooltip: labels.rec_review.dashboard.tooltip.occ_pct,
		valueFormatter: (params: ValueFormatterParams) => {
			return oneDecimalFormatter(params?.value);
		},
		cellRenderer: (params: ICellRendererParams) => {
			return renderLiveData(
				{ ...params },
				'occ_pct_live',
				'occ_pct',
				false,
				oneDecimalFormatter,
			);
		},
		comparator: liveDataComparator('occ_pct'),
		source: ColumnSources.KPI,
	},
	{
		headerName: 'Occ %\n(LYST)',
		field: 'occ_pct_lyst',
		headerTooltip: labels.rec_review.dashboard.tooltip.occ_pct_lyst,
		valueFormatter: (params: ValueFormatterParams) => {
			return oneDecimalFormatter(params?.value);
		},
		source: ColumnSources.KPI,
	},
	{
		headerName: 'Occ %\n(LYF)',
		field: 'occ_pct_lyf',
		headerTooltip: labels.rec_review.dashboard.tooltip.occ_pct_lyf,
		valueFormatter: (params: ValueFormatterParams) => {
			return oneDecimalFormatter(params?.value);
		},
		source: ColumnSources.KPI,
	},
	{
		headerName: 'Curr\nRev',
		field: 'current_revenue_live',
		headerTooltip: labels.rec_review.dashboard.tooltip.current_revenue,
		valueFormatter: (params: ValueFormatterParams) => {
			return thousandSeparatorRateFormatter(params?.value);
		},
		cellRenderer: (params: ICellRendererParams) => {
			return renderLiveData(
				{ ...params },
				'current_revenue_live',
				'current_revenue',
				true,
				thousandSeparatorRateFormatter,
				true,
			);
		},
		comparator: liveDataComparator('current_revenue'),
		source: ColumnSources.KPI,
	},
	{
		headerName: 'Fcst\nRev',
		field: 'fcst_rev',
		headerTooltip: labels.rec_review.dashboard.tooltip.fcst_rev,
		valueFormatter: (params: ValueFormatterParams) => {
			return thousandSeparatorRateFormatter(params?.value);
		},
		cellRenderer: (params: ICellRendererParams) => {
			return (
				<LiveDataField
					liveVal={null}
					oldVal={params.value?.toString()}
					enableCurrencyFormatter={true}
					removeDecimals={true}
				/>
			);
		},
		source: ColumnSources.KPI,
	},
];
